// src/pages/customers/[id]/tabs/creditNotes.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../../../../utils/config";

const moneyINR = (v) =>
  `₹${(Number(v || 0)).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDateDDMMYYYY = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

// Compute current FY for India: Apr 1 – Mar 31
function getCurrentFYRange() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0=Jan
  const fyStartYear = month >= 3 ? year : year - 1; // Apr (3) or later → same year else previous
  const start = new Date(fyStartYear, 3, 1);        // Apr 1
  const end = new Date(fyStartYear + 1, 2, 31);     // Mar 31 next year
  const toYMD = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
  return { from: toYMD(start), to: toYMD(end) };
}

export default function CreditNotesTab() {
  const { id } = useParams(); // customer id

  // Filters (default = current FY)
  const fy = useMemo(getCurrentFYRange, []);
  const [from, setFrom] = useState(fy.from);
  const [to, setTo] = useState(fy.to);

  const [rows, setRows] = useState([]);
  const [sums, setSums] = useState({
    sum_credit_note_amount: 0,
    sum_taxable: 0,
    sum_gst: 0,
    sum_deduction: 0,
  });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // basic pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((Number(total) || 0) / pageSize)),
    [total, pageSize]
  );

  async function fetchNotes(signal) {
    try {
      setLoading(true);
      setErr("");
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${API_BASE_URL}/api/credit?customer_id=${id}&page=${page}&page_size=${pageSize}&from=${from}&to=${to}`,
        { headers: { Authorization: `Bearer ${token}` }, signal }
      );
      const data = res.data || {};
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setSums(data.sums || {});
      setTotal(data.total || 0);
    } catch (e) {
      if (axios.isCancel(e)) return;
      console.error("Failed to load credit notes:", e);
      setErr("Failed to load credit notes");
    } finally {
      setLoading(false);
    }
  }

  // load on mount, when customer, pagination, or date range changes
  useEffect(() => {
    const controller = new AbortController();
    fetchNotes(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, page, from, to]);

  // Reset pagination when filters change via UI button
  const applyFilters = () => setPage(1);
  const resetFY = () => {
    setFrom(fy.from);
    setTo(fy.to);
    setPage(1);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 w-48 bg-gray-200 rounded" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-100 rounded" />
            ))}
          </div>
          <div className="h-64 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-blue-900">Credit Notes</h2>
          <div className="text-xs text-gray-500 mt-1">
            Page {page} of {totalPages} • {total} total
          </div>
        </div>

        {/* Date range filter */}
        <div className="bg-white rounded-xl shadow p-3 flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">From</label>
            <input
              type="date"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">To</label>
            <input
              type="date"
              value={to}
              min={from}
              onChange={(e) => setTo(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={applyFilters}
              className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700"
            >
              Apply
            </button>
            <button
              onClick={resetFY}
              className="px-3 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm hover:bg-gray-200"
              title={`Reset to FY ${fy.from} → ${fy.to}`}
            >
              Current FY
            </button>
          </div>
        </div>
      </div>

      {err && <div className="text-red-600">{err}</div>}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl shadow p-4">
          <div className="text-xs text-gray-500">Taxable</div>
          <div className="text-lg font-semibold">
            {moneyINR(sums?.sum_taxable || 0)}
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-4">
          <div className="text-xs text-gray-500">GST</div>
          <div className="text-lg font-semibold">
            {moneyINR(sums?.sum_gst || 0)}
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-4">
          <div className="text-xs text-gray-500">Deduction</div>
          <div className="text-lg font-semibold">
            {moneyINR(sums?.sum_deduction || 0)}
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-4">
          <div className="text-xs text-gray-500">Total CN Amount</div>
          <div className="text-lg font-semibold">
            {moneyINR(sums?.sum_credit_note_amount || 0)}
          </div>
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto rounded-xl shadow bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-4 py-3 text-left">CN #</th>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-left">Items</th>
              <th className="px-4 py-3 text-right">Taxable</th>
              <th className="px-4 py-3 text-right">GST</th>
              <th className="px-4 py-3 text-right">Deduction</th>
              <th className="px-4 py-3 text-right">Total</th>
              <th className="px-4 py-3 text-left">Created By</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-blue-700">#{r.id}</td>
                <td className="px-4 py-3">
                  {formatDateDDMMYYYY(r.credit_note_date)}
                </td>
                <td className="px-4 py-3">{r.items_count || 0}</td>
                <td className="px-4 py-3 text-right">
                  {moneyINR(r.total_amount)}
                </td>
                <td className="px-4 py-3 text-right">
                  {moneyINR(r.gst_total)}
                </td>
                <td className="px-4 py-3 text-right">
                  {moneyINR(r.deduction)}
                </td>
                <td className="px-4 py-3 text-right font-semibold">
                  {moneyINR(r.credit_note_amount)}
                </td>
                <td className="px-4 py-3">{r.created_by_name || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {rows.map((r) => (
          <div
            key={r.id}
            className="bg-white rounded-xl shadow p-4 flex flex-col gap-2"
          >
            <div className="flex items-center justify-between">
              <div className="font-semibold text-blue-700">CN #{r.id}</div>
              <div className="text-sm text-gray-500">
                {formatDateDDMMYYYY(r.credit_note_date)}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="text-gray-500">Items</div>
              <div className="text-right">{r.items_count || 0}</div>

              <div className="text-gray-500">Taxable</div>
              <div className="text-right">{moneyINR(r.total_amount)}</div>

              <div className="text-gray-500">GST</div>
              <div className="text-right">{moneyINR(r.gst_total)}</div>

              <div className="text-gray-500">Deduction</div>
              <div className="text-right">{moneyINR(r.deduction)}</div>

              <div className="text-gray-500 font-medium">Total</div>
              <div className="text-right font-semibold">
                {moneyINR(r.credit_note_amount)}
              </div>
            </div>

            <div className="text-xs text-gray-500 mt-1">
              Created by: {r.created_by_name || "-"}
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1}
          className="px-3 py-1.5 rounded border text-sm disabled:opacity-50 bg-white"
        >
          Prev
        </button>
        <span className="text-sm text-gray-600">
          {page} / {totalPages}
        </span>
        <button
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages}
          className="px-3 py-1.5 rounded border text-sm disabled:opacity-50 bg-white"
        >
          Next
        </button>
      </div>
    </div>
  );
}
